import { useMemo } from 'react';
import {
  AnomalyChart,
  DateTimeRangePicker,
  DropDown,
  Flex,
  FormControls,
  InfoTooltip,
  LoadingButton,
  IconButton,
} from '@elementary/common-ui/components';
import {
  AnomalyDetectionChart,
  AutomatedTestAnomalyConfig,
  AutomatedTestConfigurationApi,
  Period,
} from '@elementary/common-ui/types';
import type { DateTimeRangeValue } from '@elementary/common-ui/components/DateTimeRangePicker';
import { Tooltip } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus-black.svg';
import { isInDetectionPeriod } from '@elementary/common-ui/components/TestCharts/utils';
import PercentageThresholdField from 'shared/components/PercentageThresholdField';
import {
  AnomalyDirectionField,
  DetectionModeField,
  DetectionPeriodField,
} from 'shared/components/VolumeAnomalyConfigSections';
import { VolumeDetectionMode } from 'shared/types';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';
import TrainingPeriodSection from './TrainingPeriodSection';
import { applyThresholdMode } from './utils';

type Props = {
  chartData: AnomalyDetectionChart;
  methods: UseFormReturn<AutomatedTestConfigurationApi>;
  onSimulate: (config: AutomatedTestAnomalyConfig) => void;
  isLoading: boolean;
  disabledSimulator?: boolean;
  readOnly?: boolean;
  savedTrainingPeriod?: Period | null;
  mode: VolumeDetectionMode;
  onModeChange: (mode: VolumeDetectionMode) => void;
};

const CloudVolumeFields = ({
  chartData,
  methods,
  onSimulate,
  isLoading,
  disabledSimulator = false,
  readOnly,
  savedTrainingPeriod,
  mode,
  onModeChange,
}: Props) => {
  const excludedTimeRanges =
    methods.watch('anomaly_config.excluded_time_ranges') || [];

  const anomalyDirection = methods.watch('anomaly_config.anomaly_direction');

  const handleModeChange = (next: VolumeDetectionMode) => {
    if (readOnly) return;
    onModeChange(next);
  };

  const handleSimulate = () => {
    onSimulate(applyThresholdMode(methods.getValues('anomaly_config'), mode));
  };

  const sortedMetrics = useMemo(
    () =>
      [...chartData.metrics].sort(
        (a, b) => dayjs(a.end_time).valueOf() - dayjs(b.end_time).valueOf(),
      ),
    [chartData.metrics],
  );

  const firstRunTime = sortedMetrics[0]?.end_time;
  const latestRunTime = sortedMetrics[sortedMetrics.length - 1]?.end_time;

  const trainingPeriodEnd = useMemo(() => {
    const trainingPeriodItems = sortedMetrics.filter(
      (item) =>
        !isInDetectionPeriod(
          chartData.detectionPeriod,
          item.end_time,
          latestRunTime,
        ),
    );

    return trainingPeriodItems[trainingPeriodItems.length - 1]?.end_time;
  }, [chartData.detectionPeriod, sortedMetrics, latestRunTime]);

  const handleAddExcludedRange = (start?: number, end?: number) => {
    if (readOnly) return;
    methods.setValue('anomaly_config.excluded_time_ranges', [
      ...excludedTimeRanges,
      {
        start_time: start
          ? dayjs(start).toISOString()
          : dayjs(firstRunTime).toISOString(),
        end_time:
          end && !dayjs(end).isAfter(dayjs(trainingPeriodEnd))
            ? dayjs(end).toISOString()
            : dayjs(trainingPeriodEnd).toISOString(),
      },
    ]);
  };

  const handleRemoveExcludedRange = (index: number) => {
    if (readOnly) return;
    methods.setValue(
      'anomaly_config.excluded_time_ranges',
      excludedTimeRanges.filter((_, i) => i !== index),
    );
  };

  const handleExcludedRangeChange = (
    index: number,
    value: DateTimeRangeValue,
  ) => {
    if (readOnly) return;
    if (!value.start?.isValid() || !value.end?.isValid()) return;

    const newRanges = excludedTimeRanges.map((item, i) =>
      i === index
        ? {
            start_time: value.start!.toISOString(),
            end_time: value.end!.toISOString(),
          }
        : item,
    );
    methods.setValue('anomaly_config.excluded_time_ranges', newRanges);
  };

  return (
    <>
      <DetectionModeField
        mode={mode}
        onChange={handleModeChange}
        readOnly={readOnly}
      />

      <AnomalyDirectionField
        control={methods.control}
        name="anomaly_config.anomaly_direction"
        readOnly={readOnly}
      />

      {mode === 'percentage_thresholds' && (
        <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
          <Flex sx={{ alignItems: 'center', gap: space('xs') }}>
            <FormControls.FormTitle>
              Fixed percentage thresholds
            </FormControls.FormTitle>
            <InfoTooltip
              size={16}
              title={
                <>
                  Compared to the previous measurement.
                  <br />
                  Example: a 3% drop threshold fails when 100 → 97.
                </>
              }
            />
          </Flex>

          <PercentageThresholdField
            control={methods.control}
            anomalyDirection={anomalyDirection}
            spikeFieldName="anomaly_config.spike_threshold"
            dropFieldName="anomaly_config.drop_threshold"
            readOnly={readOnly}
          />
        </FormControls.FieldsWrapper>
      )}

      {mode === 'anomaly_detection' && (
        <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
          <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>

          <FormControls.Row
            style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}
          >
            <FormControls.FormText>
              Adjust test thresholds by setting the sensitivity level to be
            </FormControls.FormText>
            <Controller
              control={methods.control}
              name="anomaly_config.sensitivity"
              render={({ field }) => (
                <DropDown
                  sortOptions={false}
                  options={[
                    { value: 'low', label: 'low' },
                    { value: 'medium', label: 'medium' },
                    { value: 'high', label: 'high' },
                  ]}
                  {...field}
                  disabled={readOnly || field.disabled}
                />
              )}
            />
          </FormControls.Row>
        </FormControls.FieldsWrapper>
      )}

      <DetectionPeriodField
        control={methods.control}
        register={methods.register}
        countName="anomaly_config.detection_period.count"
        periodName="anomaly_config.detection_period.period"
        readOnly={readOnly}
      />

      <TrainingPeriodSection
        methods={methods}
        readOnly={readOnly}
        savedTrainingPeriod={savedTrainingPeriod}
      />

      <FormControls.FieldsWrapper
        sx={{ gap: (theme) => `${theme.tokens.space.lg} !important` }}
      >
        <FormControls.FormTitle>Excluded time ranges</FormControls.FormTitle>
        <FormControls.FormText>
          Exclude specific time ranges from the training period
        </FormControls.FormText>

        {excludedTimeRanges.map(
          (item: { start_time: string; end_time: string }, index: number) => (
            <FormControls.Row key={index}>
              <DateTimeRangePicker
                value={{
                  start: dayjs(item.start_time),
                  end: dayjs(item.end_time),
                }}
                onChange={(value) => handleExcludedRangeChange(index, value)}
                format="YYYY-MM-DD hh:mm A"
                disableFuture
                fromLabel="From"
                toLabel="To"
                maxDateTime={dayjs(trainingPeriodEnd)}
                disabled={readOnly}
              />

              {!readOnly && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveExcludedRange(index)}
                  sx={{ color: 'status.error.main' }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </FormControls.Row>
          ),
        )}

        {!readOnly && (
          <Tooltip title="Add excluded range">
            <IconButton
              sx={{ width: 'fit-content' }}
              variant="outlined"
              size="small"
              onClick={() => handleAddExcludedRange()}
            >
              <PlusIcon />
            </IconButton>
          </Tooltip>
        )}
      </FormControls.FieldsWrapper>

      <FormControls.FieldsWrapper>
        {!readOnly && (
          <LoadingButton
            sx={{ width: 'fit-content' }}
            variant="outlined"
            loading={isLoading}
            onClick={handleSimulate}
            disabled={disabledSimulator}
          >
            Simulate configuration
          </LoadingButton>
        )}

        {chartData && (
          <AnomalyChart
            metrics={chartData.metrics}
            status={chartData.status}
            metricsTitle={chartData.metricsTitle}
            failOnZero={chartData.failOnZero}
            anomalyDirection={chartData.anomalyDirection}
            detectionPeriod={chartData.detectionPeriod}
            detectionDelay={chartData.detectionDelay}
            onExcludeRange={readOnly ? undefined : handleAddExcludedRange}
          />
        )}
      </FormControls.FieldsWrapper>
    </>
  );
};

export default CloudVolumeFields;
